import { memo, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { sanitizeUserInput } from '@/lib';
import { WishlistPhotoGrid } from '@/components';
import type { PublicWishlistWithUser } from '../../model';
import { WishlistOwnerInfo } from './WishlistOwnerInfo';

interface PublicWishlistGridCardProps {
  wishlist: PublicWishlistWithUser;
  onClick?: () => void;
  isFavorite?: boolean;
  emptyText: string; // Текст для пустого состояния
}

/**
 * Карточка публичного вишлиста с информацией о владельце и счётчиком избранного
 * 
 * Отображает:
 * - Сетку из 1-3 фотографий желаний
 * - Бейдж с количеством желаний
 * - Иконку-сердечко с количеством добавлений в избранное
 * - Информацию о владельце (аватар и имя)
 * - Название и дату события
 */
export const PublicWishlistGridCard = memo(function PublicWishlistGridCard({ 
  wishlist, 
  onClick, 
  isFavorite = false,
  emptyText,
}: PublicWishlistGridCardProps): JSX.Element {
  // Получаем фотографии желаний (максимум 4 для обложки)
  const wishPhotos = useMemo(() => {
    if (!wishlist.items) return [];
    // .filter(imageUrl) гарантирует отсутствие undefined; каст безопасен
    return wishlist.items
      .filter(item => item.imageUrl)
      .slice(0, 4)
      .map(item => item.imageUrl) as string[];
  }, [wishlist.items]);

  const itemCount = wishlist.itemCount ?? 0;
  const favoriteCount = wishlist.favoriteCount ?? 0;

  return (
    <button type="button" onClick={onClick} className="text-left w-full">
      {/* Карточка с фотографиями */}
      <Card 
        className="overflow-hidden active:scale-95 transition-transform border-border p-0"
      >
        {/* Сетка фотографий или пустое состояние */}
        <WishlistPhotoGrid 
          wishPhotos={wishPhotos}
          emptyText={emptyText}
          itemCount={itemCount}
          badgeVariant="accent"
          isFavorite={isFavorite}
        />
      </Card>

      {/* Информация о вишлисте под карточкой */}
      <div className="mt-2 px-1">
        {/* Название */}
        <h3 className="font-semibold text-sm text-foreground truncate">
          {sanitizeUserInput(wishlist.title)}
        </h3>

        {/* Владелец вишлиста */}
        {wishlist.user && (
          <WishlistOwnerInfo 
            user={wishlist.user}
            isFavorite={isFavorite}
            favoriteCount={favoriteCount}
          />
        )}
      </div>
    </button>
  );
});